/** Auto-generated by scripts/migrate-risk-assessment.py — do not edit by hand. */
export const assessmentMarkup = `<!-- Migrated from churnsignal-risk-diagnostic -->
<span class="header-label" aria-hidden="true">Churn Risk Readiness Assessment</span>
<main id="top">
      <section class="hero" id="intro" aria-labelledby="hero-title">
        <div class="eyebrow"><span></span> Customer Success Leaders</div>
        <h1 id="hero-title">Your health score can be green.<br /> <em>Churn risk may not be.</em></h1>
        <p class="hero-copy">
          Find out whether your team is identifying meaningful churn risk early—or only documenting it once the outcome is already difficult to change.
        </p>
        <div class="hero-actions">
          <button class="button button-primary" id="start-button" data-start-assessment>Assess our churn-risk system</button>
          <span class="time-note">25 questions · About 7 minutes</span>
        </div>
        <div class="resume-card hidden" id="resume-card" aria-live="polite">
          <div>
            <p class="overline">Continue where you left off</p>
            <strong id="resume-copy">Your assessment is saved on this device.</strong>
          </div>
          <div class="resume-actions">
            <button class="button button-primary" id="resume-button" type="button">Continue assessment</button>
            <button class="text-button" id="discard-progress" type="button">Start again</button>
          </div>
        </div>
        <div class="premise-card" aria-label="Diagnostic premise">
          <div class="signal-visual" aria-hidden="true">
            <div class="signal-row"><span>Account status</span><b class="green-dot">GREEN</b></div>
            <div class="signal-chart">
              <svg viewBox="0 0 460 100" role="img" aria-label="A hidden decline beneath a green account status">
                <defs>
                  <linearGradient id="signalFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#e9ff6a" stop-opacity="0.28" />
                    <stop offset="100%" stop-color="#e9ff6a" stop-opacity="0" />
                  </linearGradient>
                </defs>
                <path class="area" d="M0 15 C42 13 62 21 98 18 S160 20 196 26 S255 37 286 43 S332 53 361 65 S414 75 460 92 L460 100 L0 100 Z" />
                <path class="line" d="M0 15 C42 13 62 21 98 18 S160 20 196 26 S255 37 286 43 S332 53 361 65 S414 75 460 92" />
                <line class="threshold" x1="0" x2="460" y1="72" y2="72" />
              </svg>
              <span class="threshold-label">Rule fires here</span>
            </div>
          </div>
          <div class="premise-copy">
            <p class="overline">Why this matters</p>
            <h2>The customer changed before the score did.</h2>
            <p>
              Fixed rules can keep an account green while the customer’s value-driving behavior is already changing. With hundreds of accounts, your team cannot investigate every quiet decline. The evidence can exist long before anyone knows to look.
            </p>
          </div>
        </div>

        <div class="landing-value" aria-labelledby="value-title">
          <div class="landing-value-heading">
            <p class="overline">What the assessment tests</p>
            <h2 id="value-title">Can your current process show the team where attention genuinely belongs?</h2>
          </div>
          <div class="landing-value-copy">
            <p>Assess the system behind your health score—from the first customer change to risk detection, prioritization, and action.</p>
            <p>See whether it gives your team a realistic chance to intervene before churn becomes obvious.</p>
            <strong>Find the gap between having customer data and knowing where to act.</strong>
          </div>
        </div>

        <div class="frustration-block" aria-labelledby="frustration-title">
          <p class="overline">The question behind the churn review</p>
          <h2 id="frustration-title">“How did we not see this coming?” is not really a question about one customer.</h2>
          <div class="frustration-copy">
            <p class="frustration-lead">It is a question about whether leadership can trust the system behind the team.</p>
            <p>When a healthy-looking account churns, you are left reconstructing the warning signs, explaining why nobody acted sooner, and wondering which other accounts are quietly moving in the same direction.</p>
            <p>Your team should not need to personally inspect every customer to feel confident that meaningful risk will surface.</p>
          </div>
        </div>

        <div class="landing-cta" aria-labelledby="landing-cta-title">
          <div>
            <p class="overline">A practical diagnostic, not a benchmark quiz</p>
            <h2 id="landing-cta-title">Find out where your churn-risk system breaks down.</h2>
            <p>Complete the assessment and enter your email to receive a PDF detailing your results, plus the False-Green Review Workbook for reviewing customers that churned while still appearing healthy.</p>
          </div>
          <div class="landing-cta-action">
            <button class="button button-primary" data-start-assessment>Find my churn-risk blind spot</button>
            <span>Detailed PDF report · False-Green Review Workbook</span>
          </div>
        </div>
      </section>

      <section class="context-step hidden" id="context-step" aria-labelledby="context-title">
        <div class="context-copy">
          <p class="eyebrow"><span></span> Your starting point</p>
          <h2 id="context-title">Start with the customers your team supports.</h2>
          <p>These details help us read your answers against the work your team covers today.</p>
          <div class="context-principle">
            <strong>Accounts per CSM is context, not a target.</strong>
            <span>We use it to understand whether your current approach can cover more customers as you grow.</span>
          </div>
        </div>
        <form class="context-form" id="context-form" novalidate>
          <div class="form-grid">
            <div class="form-field">
              <label for="context-name">Name</label>
              <input id="context-name" name="full_name" type="text" autocomplete="name" placeholder="Your name" required />
            </div>
            <div class="form-field">
              <label for="context-email">Work email (optional)</label>
              <input id="context-email" name="work_email" type="email" autocomplete="email" placeholder="you@company.com" />
              <small>Optional. Your answers are saved on this device. Add email to receive your PDF + workbook. No newsletter.</small>
            </div>
            <div class="form-field">
              <label for="context-company">Company</label>
              <input id="context-company" name="company_name" type="text" autocomplete="organization" placeholder="Company name" required />
            </div>
            <div class="form-field">
              <label for="context-role">Your role</label>
              <select id="context-role" name="role" required>
                <option value="" selected disabled>Select role</option>
                <option>Head / VP of Customer Success</option>
                <option>CS Team Lead</option>
                <option>Customer Success Manager</option>
                <option>CS Ops / RevOps</option>
                <option>Founder / Executive</option>
                <option>Other</option>
              </select>
            </div>
            <div class="form-field">
              <label for="context-accounts">Active customer accounts</label>
              <input id="context-accounts" name="active_accounts" type="number" inputmode="numeric" min="1" step="1" placeholder="e.g. 750" required />
            </div>
            <div class="form-field">
              <label for="context-csms">CSMs managing customer accounts</label>
              <input id="context-csms" name="csm_count" type="number" inputmode="numeric" min="1" step="1" placeholder="e.g. 6" required />
              <small>Include only CSMs who manage a customer portfolio.</small>
            </div>
          </div>
          <button class="button button-primary context-button" type="submit">Continue to the assessment</button>
          <p class="form-note" id="context-form-note">Takes about 7 minutes. If you leave, you can resume on this device.</p>
        </form>
      </section>

      <section class="assessment hidden" id="assessment" aria-live="polite">
        <div class="assessment-shell">
          <div class="assessment-topline">
            <button class="text-button" id="back-button" aria-label="Go to previous question">Back</button>
            <div class="progress-copy"><span id="progress-number">1</span> / 25</div>
          </div>
          <div class="progress-track" aria-hidden="true"><div id="progress-bar"></div></div>
          <div id="question-container"></div>
        </div>
        <aside class="dimension-rail" aria-label="Dimensions being assessed">
          <p class="overline">What we check</p>
          <ol id="dimension-list">
            <li data-dimension="signals"><span>01</span>Risk signals</li>
            <li data-dimension="model"><span>02</span>Health score accuracy</li>
            <li data-dimension="priority"><span>03</span>Account focus</li>
            <li data-dimension="early"><span>04</span>Early warning</li>
            <li data-dimension="action"><span>05</span>Risk response</li>
            <li data-dimension="capacity"><span>06</span>Portfolio coverage</li>
            <li data-dimension="learning"><span>07</span>What works</li>
          </ol>
          <p class="rail-note">This checks your process. It does not grade your CS team.</p>
        </aside>
      </section>

      <section class="results hidden" id="results" aria-live="polite">
        <div class="results-intro report-block">
          <div class="result-score-wrap">
            <div class="score-ring" id="score-ring">
              <div><strong id="overall-score">0</strong><span>/100</span></div>
            </div>
            <span class="score-label">Churn Risk System Score</span>
            <p class="score-breakdown" id="score-breakdown"></p>
          </div>
          <div class="result-headline">
            <p class="eyebrow"><span></span> Your result</p>
            <span class="stage-badge" id="maturity-stage">Signal-aware</span>
            <h2 id="result-title">The Green Blind Spot</h2>
            <p id="result-summary"></p>
          </div>
        </div>

        <div class="core-finding report-block">
          <span>What this means</span>
          <p id="core-finding-copy"></p>
        </div>

        <div class="primary-concern report-block">
          <div class="concern-heading">
            <p class="overline" id="primary-label">Your biggest gap</p>
            <span class="focus-tag" id="focus-tag">Risk signals</span>
            <h2 id="constraint-title"></h2>
          </div>
          <div class="concern-grid">
            <div>
              <span>What is happening</span>
              <p id="constraint-copy"></p>
            </div>
            <div>
              <span>Why it matters</span>
              <p id="business-exposure"></p>
            </div>
            <div>
              <span id="secondary-label">What makes it worse</span>
              <strong id="secondary-focus"></strong>
              <p id="secondary-copy"></p>
            </div>
          </div>
        </div>

        <section class="business-impact report-block" aria-labelledby="business-impact-title">
          <div class="business-impact-heading">
            <p class="overline">Business impact</p>
            <h2 id="business-impact-title">What it means for the business</h2>
            <p id="business-impact-summary"></p>
          </div>
          <div class="business-impact-grid">
            <article>
              <span>Recurring revenue exposure</span>
              <p id="revenue-impact"></p>
            </article>
            <article>
              <span>CS capacity cost</span>
              <p id="capacity-impact"></p>
            </article>
            <article>
              <span>Growth and expansion</span>
              <p id="growth-impact"></p>
            </article>
          </div>
          <p class="business-impact-note">These are areas of business exposure based on your answers. They are not attributed revenue-loss estimates.</p>
        </section>

        <div class="radar-card report-block">
          <div class="card-heading">
            <div>
              <p class="overline">Your risk profile</p>
              <h3>Where your process works and where it slows down</h3>
            </div>
            <span class="legend"><i></i> Higher means stronger</span>
          </div>
          <div class="radar-chart-wrap">
            <svg
              class="radar-chart"
              id="radar-chart"
              viewBox="0 0 680 520"
              role="img"
              aria-label="Churn risk readiness across seven areas"
            >
            </svg>
          </div>
        </div>

        <div class="result-grid">
          <div class="dimension-card report-block">
            <div class="card-heading">
              <div>
                <p class="overline">Your score by area</p>
                <h3>Seven parts of churn risk readiness</h3>
              </div>
              <span class="legend"><i></i> Your score</span>
            </div>
            <div id="dimension-results"></div>
          </div>

        </div>

        <div class="evidence-section report-block">
          <div class="section-heading">
            <p class="overline">What your answers show</p>
            <h2 id="evidence-title">Why you received this result</h2>
            <p id="evidence-intro">These findings come from your answers, not from a generic company benchmark.</p>
          </div>
          <div class="evidence-grid" id="evidence-list"></div>
        </div>

        <div class="recommendation-card report-block">
          <div class="recommendation-lead">
            <p class="overline" id="recommendation-overline">Leadership next step</p>
            <h2 id="next-step-title"></h2>
            <p id="next-step-copy"></p>
          </div>
          <div class="recommendation-grid">
            <div>
              <span>Why this first</span>
              <p id="why-now-copy"></p>
            </div>
            <div>
              <span>Track</span>
              <p id="measure-copy"></p>
            </div>
            <div>
              <span>You will know it is working when</span>
              <p id="success-copy"></p>
            </div>
            <div class="avoid-block">
              <span>Do not start with</span>
              <p id="not-this-copy"></p>
            </div>
          </div>
        </div>

        <section class="operating-limits report-block" id="operating-limits" aria-labelledby="limits-title">
          <div class="operating-limits-copy">
            <p class="overline">Make the next decision explicit</p>
            <h2 id="limits-title">Define when your current approach stops being reliable.</h2>
            <p>Set the two operating limits leadership will use to decide when the way you monitor churn risk needs to change. These are business guardrails, not implementation tasks.</p>
          </div>
          <form class="limit-form no-print" id="limit-form" novalidate>
            <div class="limit-fields">
              <div class="form-field">
                <label for="grr-floor">Minimum acceptable GRR</label>
                <div class="number-suffix">
                  <input id="grr-floor" name="grr_floor" type="number" inputmode="decimal" min="0" max="100" step="0.1" placeholder="e.g. 92" required />
                  <span>%</span>
                </div>
                <small>The floor that would trigger a leadership response.</small>
              </div>
              <div class="form-field">
                <label for="portfolio-limit">Maximum manageable accounts per CSM</label>
                <input id="portfolio-limit" name="portfolio_limit" type="number" inputmode="decimal" min="1" step="0.1" placeholder="e.g. 75" required />
                <small>The highest load that still allows reliable coverage.</small>
              </div>
            </div>
            <button class="button button-primary" type="submit">Set our operating limits</button>
            <p class="form-note" id="limit-form-note">Use leadership judgement. You do not need new telemetry to set these guardrails.</p>
          </form>
          <div class="limit-result hidden" id="limit-result">
            <span class="stage-badge" id="limit-status">Inside your limits</span>
            <h3 id="limit-result-title"></h3>
            <p id="limit-result-copy"></p>
            <div class="limit-result-grid">
              <div><span>Current load</span><strong id="current-load"></strong></div>
              <div><span>Your capacity limit</span><strong id="chosen-limit"></strong></div>
              <div><span>Your GRR floor</span><strong id="chosen-grr-floor"></strong></div>
            </div>
            <button class="text-button no-print" id="edit-limits" type="button">Edit limits</button>
          </div>
        </section>

        <div class="report-delivery report-block no-print">
          <div>
            <p class="overline">Your report + workbook</p>
            <h2>Keep the result and your operating limits.</h2>
            <p>Enter your email to receive a PDF with your result, business context, evidence, and the limits you set. You will also receive the False-Green Review Workbook.</p>
          </div>
          <div class="report-delivery-action">
            <button class="button button-primary" id="open-report-form" type="button">Email me the PDF + workbook</button>
            <form class="report-email-form hidden" id="report-email-form" novalidate>
              <label for="report-email">Work email</label>
              <div class="report-email-row">
                <input id="report-email" name="report_email" type="email" autocomplete="email" placeholder="you@company.com" required />
                <button class="button" type="submit">Send both</button>
              </div>
              <p class="form-note" id="report-form-note">Confirm where you want the PDF report and workbook sent. You can change the email before submitting.</p>
            </form>
            <a class="button button-secondary report-download hidden" id="report-download" href="#" download>Download PDF report</a>
            <a class="button button-secondary report-download hidden" id="workbook-download" href="/risk-assessment/ChurnSignal_False_Green_Review_Workbook.xlsx" download>Download workbook</a>
          </div>
        </div>

        <div class="cta-panel hidden" id="cta-panel">
          <div>
            <p class="overline" id="cta-overline">Free churn-risk review</p>
            <h2 id="cta-title">Turn this result into one practical retention decision.</h2>
            <p id="cta-copy">
              In a free 30-minute working session, we will review your assessment, pressure-test the biggest gap, and define one change your team can make and measure with the data you already have.
            </p>
          </div>
          <form id="cta-form" novalidate>
            <input id="full-name" name="full_name" type="hidden" />
            <input id="company-name" name="company_name" type="hidden" />
            <input id="role" name="role" type="hidden" />
            <input id="customer-accounts" name="active_accounts" type="hidden" />
            <input id="csm-count" name="csm_count" type="hidden" />
            <div class="form-field cta-email-field">
              <label for="work-email">Work email</label>
              <input id="work-email" name="work_email" type="email" autocomplete="email" placeholder="you@company.com" required />
            </div>
            <button class="button review-button" id="cta-button" type="submit">Request a free churn-risk review</button>
            <p class="form-note" id="form-note">We’ll use your assessment to prepare. No product demo required. We’ll use your email only to arrange the session.</p>
          </form>
          <div class="cta-share-action hidden" id="cta-share-action">
            <button class="button review-button" id="cta-share-button" type="button">Share the assessment</button>
            <p class="form-note">Your report email includes the False-Green Review Workbook.</p>
          </div>
        </div>

        <details class="leadership-notes report-block">
          <summary>View the leadership notes</summary>
          <div class="brief-preview" id="leadership-brief-copy"></div>
          <div class="brief-actions no-print">
            <button class="button button-secondary" id="copy-results">Copy leadership notes</button>
          </div>
        </details>

        <details class="methodology report-block">
          <summary>How we calculate your score</summary>
          <div>
            <p>The Churn Risk System Score uses 21 process questions across seven areas, plus four business-context questions. Risk signals, health classification accuracy, and early warning carry the most process weight. Your lowest process area also lowers the process score because one weak step can break the path from risk to action.</p>
            <p>“We do not measure this” and “this has not happened yet” are treated as missing or not applicable, not as zero. NRR and GRR contribute to the final system score when available. Companies with fewer than 100 active accounts receive a scale-adjusted interpretation rather than a maturity grade. This is a structured self-assessment, not an industry benchmark.</p>
          </div>
        </details>

        <div class="result-actions no-print">
          <button class="text-button restart" id="restart-button">Retake assessment</button>
        </div>
      </section>
    </main>
<div class="toast" id="toast" role="status" aria-live="polite"></div>
`;
